/**
 * M11: star illustration (SVG) for the system panel – the same look per star type as on the map
 * (graphics/pixi-art.ts), drawn from the shared tokens. Purely decorative: it shows the star TYPE the server
 * already revealed for this system, never planets or resources (those are not invented here).
 */
import { memo, useId } from 'react';
import { cssOf, starStyle } from '../design/tokens.ts';

export const StarArt = memo(function StarArt({ type, size = 64, className }: { type: string; size?: number; className?: string }) {
  const uid = useId().replace(/:/g, '');
  const st = starStyle(type);
  const glow = cssOf(st.glow), core = cssOf(st.core);
  const r = 9 + st.radius * 1.1; // core radius in the 100×100 box
  const g = `sg-${uid}`;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" role="img" aria-label={st.label} className={className} data-star-art={type}>
      <defs>
        <radialGradient id={g}>
          <stop offset="0" stopColor={glow} stopOpacity="0.9" />
          <stop offset="0.35" stopColor={glow} stopOpacity="0.35" />
          <stop offset="1" stopColor={glow} stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${g}c`}>
          <stop offset="0" stopColor="#fff" />
          <stop offset="0.5" stopColor={core} />
          <stop offset="1" stopColor={glow} />
        </radialGradient>
      </defs>

      {type === 'black_hole' ? (
        <>
          <circle cx="50" cy="50" r="46" fill={`url(#${g})`} opacity="0.55" />
          <ellipse cx="50" cy="50" rx="40" ry="12" fill="none" stroke={glow} strokeWidth="5" opacity="0.35" transform="rotate(-18 50 50)" />
          <ellipse cx="50" cy="50" rx="34" ry="9" fill="none" stroke="#ffd9a0" strokeWidth="2.2" opacity="0.9" transform="rotate(-18 50 50)" />
          <circle cx="50" cy="50" r="15" fill="#000" stroke="#ffcf8a" strokeWidth="1.4" opacity="1" />
          <path d="M16 44 Q50 26 84 44" fill="none" stroke="#ffe6c2" strokeWidth="1.2" opacity="0.55" />
        </>
      ) : type === 'binary' ? (
        <>
          <circle cx="50" cy="50" r="48" fill={`url(#${g})`} opacity="0.7" />
          <ellipse cx="50" cy="50" rx="26" ry="9" fill="none" stroke="#fff" strokeWidth="0.8" opacity="0.18" transform="rotate(-24 50 50)" />
          <circle cx="36" cy="58" r={r * 0.78} fill={`url(#${g}c)`} />
          <circle cx="65" cy="42" r={r * 0.55} fill="#ffb19a" stroke="#ffe0d6" strokeWidth="0.8" />
        </>
      ) : (
        <>
          <circle cx="50" cy="50" r="48" fill={`url(#${g})`} />
          {type === 'neutron_star' && <path d="M50 4 V96" stroke={glow} strokeWidth="2.4" opacity="0.7" strokeLinecap="round" />}
          {(type === 'blue_giant' || type === 'yellow_dwarf' || type === 'white_dwarf') && (
            <g stroke={core} strokeLinecap="round" opacity="0.6">
              <path d="M50 8 V92 M8 50 H92" strokeWidth="1.4" />
              {type === 'blue_giant' && <path d="M20 20 L80 80 M80 20 L20 80" strokeWidth="1" opacity="0.6" />}
            </g>
          )}
          <circle cx="50" cy="50" r={r} fill={`url(#${g}c)`} />
        </>
      )}
    </svg>
  );
});
